/**
 * NEXUS UI - Toggle
 * Futuristic on/off switch
 */

import React from 'react';
import type { BaseProps, NexusVariant, NexusSize } from './types';

export interface ToggleProps extends BaseProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  variant?: NexusVariant;
  size?: NexusSize;
  label?: string;
  labelPosition?: 'left' | 'right';
  disabled?: boolean;
}

const variantColors: Record<NexusVariant, { base: string; glow: string }> = {
  cyan: { base: '#00d4ff', glow: 'rgba(0, 212, 255, 0.6)' },
  blue: { base: '#3b82f6', glow: 'rgba(59, 130, 246, 0.6)' },
  violet: { base: '#a855f7', glow: 'rgba(168, 85, 247, 0.6)' },
  green: { base: '#00ff88', glow: 'rgba(0, 255, 136, 0.6)' },
  amber: { base: '#ffaa00', glow: 'rgba(255, 170, 0, 0.6)' },
  red: { base: '#ff3366', glow: 'rgba(255, 51, 102, 0.6)' },
};

const sizeConfig: Record<NexusSize, { width: number; height: number; thumb: number }> = {
  sm: { width: 36, height: 20, thumb: 14 },
  md: { width: 48, height: 26, thumb: 18 },
  lg: { width: 60, height: 32, thumb: 24 },
  xl: { width: 72, height: 38, thumb: 30 },
};

export const Toggle: React.FC<ToggleProps> = ({
  checked,
  onChange,
  variant = 'cyan',
  size = 'md',
  label,
  labelPosition = 'right',
  disabled = false,
  className = '',
  style,
}) => {
  const colors = variantColors[variant];
  const config = sizeConfig[size];
  const thumbOffset = checked ? config.width - config.thumb - 4 : 4;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!disabled) onChange(!checked);
    }
  };

  return (
    <label
      className={`nx-toggle ${className}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        flexDirection: labelPosition === 'left' ? 'row-reverse' : 'row',
        ...style,
      }}
    >
      {/* Track */}
      <div
        role="switch"
        aria-checked={checked}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
        onClick={() => !disabled && onChange(!checked)}
        style={{
          position: 'relative',
          width: config.width,
          height: config.height,
          borderRadius: config.height / 2,
          background: checked ? `${colors.base}30` : 'var(--nx-void-elevated)',
          border: `1px solid ${checked ? colors.base : 'rgba(255, 255, 255, 0.1)'}`,
          transition: 'all 0.2s ease-out',
          boxShadow: checked ? `0 0 12px ${colors.glow}, inset 0 0 8px ${colors.glow}` : 'none',
          outline: 'none',
        }}
      >
        {/* Inner glow track when checked */}
        {checked && (
          <div
            style={{
              position: 'absolute',
              left: 4,
              top: '50%',
              transform: 'translateY(-50%)',
              width: thumbOffset - 4,
              height: 3,
              borderRadius: 2,
              background: colors.base,
              opacity: 0.4,
              transition: 'width 0.2s ease-out',
            }}
          />
        )}

        {/* Thumb */}
        <div
          style={{
            position: 'absolute',
            left: thumbOffset,
            top: '50%',
            transform: 'translateY(-50%)',
            width: config.thumb,
            height: config.thumb,
            borderRadius: '50%',
            background: checked ? colors.base : 'var(--nx-text-tertiary)',
            boxShadow: checked ? `0 0 8px ${colors.glow}` : '0 2px 4px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.2s ease-out',
          }}
        >
          {/* Inner dot */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: config.thumb * 0.4,
              height: config.thumb * 0.4,
              borderRadius: '50%',
              background: checked ? 'rgba(255, 255, 255, 0.8)' : 'var(--nx-void-surface)',
              transition: 'background 0.2s ease-out',
            }}
          />
        </div>
      </div>

      {/* Label */}
      {label && (
        <span
          style={{
            fontFamily: 'var(--nx-font-interface)',
            fontSize: size === 'sm' ? '11px' : size === 'xl' ? '14px' : '12px',
            color: checked ? 'var(--nx-text-primary)' : 'var(--nx-text-secondary)',
            transition: 'color 0.2s ease-out',
            userSelect: 'none',
          }}
        >
          {label}
        </span>
      )}
    </label>
  );
};

export default Toggle;
